import { useState } from 'react';
import {
  customMillisecondsToHours,
  shiftDateFormat,
} from '../../lib/utils/date-time';
import { ShiftPeriod, ShiftCellRecord, shiftComponentTopOffset } from './types';
import ShiftManager from './ShiftManager';
import { Button, Col, Modal, Row } from 'antd';
import { format } from 'date-fns';
import Tooltip from 'antd/es/tooltip';
import { millisecondsPerDay } from './lib/constants';
import { shiftComponentTitleWidth } from './Shift/ShiftDrawer';

type ShiftCellProps = {
  readonly cellRecord: ShiftCellRecord;
  readonly dayShift: ShiftPeriod;
};

type AddShiftButtonProps = {
  readonly cellRecord: ShiftCellRecord;
  readonly defaultHours: ShiftPeriod;
  readonly period: 'day' | 'night';
};

/**
 * Renders a button to add a new Shift. Toggles a modal with empty Shift data.
 */
const AddShiftButton = ({
  cellRecord,
  defaultHours,
  period,
}: AddShiftButtonProps) => {
  const [showShiftModal, setShowShiftModal] = useState(false);

  return (
    <>
      <Tooltip
        title={`[${format(
          cellRecord.selectedDate,
          shiftDateFormat
        )}] Add ${period} shift for [${cellRecord.roleName}]`}
        overlayStyle={{ maxWidth: '500px' }}
      >
        <Button
          id={`add-shift-btn-${format(cellRecord.selectedDate, 'yyyy-MM-dd')}-${
            cellRecord.teamRoleAssignmentId
          }-${period}`}
          className="add-shift-btn"
          block
          onClick={() => setShowShiftModal(true)}
          style={{
            background: 'none',
            height: '100%',
            fontSize: '18px',
            opacity: 0.6,
          }}
        >
          {period === 'day' ? '\u263C' : '\u263D'}
        </Button>
      </Tooltip>

      <Modal
        className="shift-modal"
        title="New Shift"
        open={showShiftModal}
        footer={null}
        destroyOnClose
        onCancel={() => setShowShiftModal(false)}
      >
        <ShiftManager
          titleWidth={shiftComponentTitleWidth}
          record={cellRecord}
          defaultHours={defaultHours}
          onFinish={() => setShowShiftModal(false)}
        />
      </Modal>
    </>
  );
};

/**
 * Renders controls for the Shift of a selected day. E.g. add shift buttons.
 */
const ShiftControl = ({ dayShift, cellRecord }: ShiftCellProps) => {
  // AntD columns spans must be integers between 1 and 24.
  // Since the time may be given with minutes, round it to the nearest
  // hour for the sake of the display.
  const dayShiftButtonSpan = Math.round(
    ((dayShift.end - dayShift.start) / millisecondsPerDay) * 24
  );

  const nightShiftButtonSpan = 24 - dayShiftButtonSpan;

  const defaultHours = {
    day: {
      start: customMillisecondsToHours(dayShift.start),
      end: customMillisecondsToHours(dayShift.end),
    },
    night: {
      start: customMillisecondsToHours(dayShift.end),
      end: customMillisecondsToHours(dayShift.start) + 24,
    },
  };

  const addShiftButtonLeft = `calc(100% - ${
    (1 - dayShift.start / millisecondsPerDay) * 100
  }%)`;

  return (
    <div
      className="shift-control"
      style={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        bottom: 0,
        zIndex: 1,
        marginTop: shiftComponentTopOffset,
        left: addShiftButtonLeft,
      }}
    >
      <Row style={{ height: '100%' }}>
        <Col span={dayShiftButtonSpan} style={{ height: '100%' }}>
          <AddShiftButton
            cellRecord={cellRecord}
            defaultHours={defaultHours.day}
            period={'day'}
          />
        </Col>
        <Col span={nightShiftButtonSpan}>
          <AddShiftButton
            cellRecord={cellRecord}
            defaultHours={defaultHours.night}
            period={'night'}
          />
        </Col>
      </Row>
    </div>
  );
};

export default ShiftControl;
